import React, { useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { isEmptyObject } from "../../../../utils/helpers";
import EmptySummary from "../CaSchedules/utils/EmptySummary";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../utils/export/componentExporter";

const HirePurchaseSummary = ({
  summaryData,
  caTypeTotals,
  yearOfAssessment,
}) => {
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "Hire Purchase Summary");
    }
  };

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  if (!summaryData || isEmptyObject(summaryData)) {
    return <EmptySummary caType='Hire Purchase' dataType='an asset' />;
  }

  const caTypes = Object.keys(summaryData);
  const s19Types = caTypes.filter((caType) => caType === "S19");
  const nonS19Types = caTypes.filter((caType) => caType !== "S19");

  const renderRows = (types, isS19Table) =>
    types.map((caType, caTypeIndex) => {
      const caTypeYears = summaryData[caType];
      const caTypeTotal = caTypeTotals[caType];

      return (
        <React.Fragment key={`${isS19Table ? "s19" : "normal"}-${caType}`}>
          {Object.values(caTypeYears).map((yearData, yearIndex) => (
            <TableRow
              key={`${caType}-${yearData.year}-${yearIndex}`}
              sx={{
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: yearIndex === 0 ? 600 : 400,
                  color: yearIndex === 0 ? "primary.main" : "text.primary",
                  pl: yearIndex === 0 ? 3 : 6,
                }}
              >
                {yearIndex === 0 ? caType : ""}
              </TableCell>
              <TableCell align='center'>{yearData.year}</TableCell>
              <TableCell align='center'>
                {formatAmount(yearData.principalPaid)}
              </TableCell>
              {isS19Table && (
                <>
                  <TableCell align='center'>
                    {yearData.iaDisplay === "Forfeit"
                      ? "Forfeit"
                      : formatAmount(yearData.ia || 0)}
                  </TableCell>
                  <TableCell align='center'>
                    {formatAmount(yearData.aa || 0)}
                  </TableCell>
                </>
              )}
              <TableCell align='center'>{yearData.noOfYears}</TableCell>
              <TableCell align='center'>{yearData.noOfYearsBf}</TableCell>
              <TableCell align='center'>
                {yearData.deferred ? "Yes" : "No"}
              </TableCell>
              {!isS19Table && (
                <TableCell align='right'>
                  {formatAmount(yearData.twdvBf)}
                </TableCell>
              )}
            </TableRow>
          ))}

          <TableRow
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              borderTop: "2px solid",
              borderColor: (theme) => alpha(theme.palette.divider, 0.3),
            }}
          >
            <TableCell
              sx={{
                fontWeight: 700,
                color: "primary.main",
                pl: 3,
              }}
            >
              {caType} Total
            </TableCell>
            <TableCell></TableCell>
            <TableCell
              align='center'
              sx={{
                fontWeight: 700,
                color: "primary.main",
              }}
            >
              {formatAmount(
                Object.values(caTypeYears).reduce(
                  (sum, item) => sum + (item.principalPaid || 0),
                  0
                )
              )}
            </TableCell>
            {isS19Table && (
              <>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                  }}
                >
                  {formatAmount(
                    Object.values(caTypeYears).reduce(
                      (sum, item) => sum + (item.ia || 0),
                      0
                    )
                  )}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                  }}
                >
                  {formatAmount(
                    Object.values(caTypeYears).reduce(
                      (sum, item) => sum + (item.aa || 0),
                      0
                    )
                  )}
                </TableCell>
              </>
            )}
            <TableCell colSpan={3}></TableCell>
            {!isS19Table && (
              <TableCell
                align='right'
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                }}
              >
                {formatAmount(caTypeTotal)}
              </TableCell>
            )}
          </TableRow>

          {caTypeIndex < types.length - 1 && (
            <TableRow>
              <TableCell
                colSpan={isS19Table ? 8 : 7}
                sx={{ height: 16 }}
              ></TableCell>
            </TableRow>
          )}
        </React.Fragment>
      );
    });

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
      ref={exportRef}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant='h6' fontWeight={600} color='primary.main'>
              Hire Purchase Summary - Year of Assessment {yearOfAssessment}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Principal paid amounts for the current year of assessment
            </Typography>
          </Box>
          <CameraExportButton
            onClick={handleExportAsImage}
            tooltipTitle='Screenshot of the Hire Purchase Summary'
          />
        </Box>
      </Box>

      <Box>
        {nonS19Types.length > 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                  >
                    CA Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    Principal Year
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    Principal Paid
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    No of Years
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    No of Years b/f
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    Deferred in YA {yearOfAssessment}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    TWDV b/f
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderRows(nonS19Types, false)}</TableBody>
            </Table>
          </TableContainer>
        )}

        {nonS19Types.length > 0 && s19Types.length > 0 && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              my: 2,
              width: "100%",
            }}
          />
        )}

        {s19Types.length > 0 && (
          <TableContainer
            sx={{
              borderTop: nonS19Types.length > 0 ? "1px solid" : "none",
              borderColor: "divider",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                  >
                    CA Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    Principal Year
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    Principal Paid
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    IA
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    AA
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    No of Years
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    No of Years b/f
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    }}
                    align='center'
                  >
                    Deferred in YA {yearOfAssessment}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderRows(s19Types, true)}</TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Paper>
  );
};

export default HirePurchaseSummary;
